/**
 * Каркас мини-игры (план 2b, Task 8): карточка в рамке чернилами, как
 * решение. Заголовок Piazzolla 22px (`minigame.title.<id>`), строка
 * ожидания для азартных игр (`def.expectancyText` — «в среднем теряете
 * X % ставки»), таймер 30 с в моно охрой (тикает раз в секунду, по
 * истечении — ожидаемый исход `def.expected`), кнопка «Пропустить» (тоже
 * ожидаемый исход) и блок помощи при тяжёлой теме (`heavy`).
 *
 * Внутри — экран мини-игры по `def.id` из `SCREENS` (раздел E — Task 9,
 * F/G — Task 10); экран получает `state` и `onInput`, а исход считает сам
 * каркас через `def.play(state, input, life, rng)`; экран может передать
 * вторым аргументом продвинутое состояние (блэкджек). Пока экрана нет —
 * короткое пояснение и только «Пропустить». Никаких монет Telegram:
 * ставки и выигрыши — в долларах ППС жизни.
 */
import type { Life, MinigameOutcome, Rng } from '@life/engine';
import { t } from '@life/i18n';
import type { MinigameDef } from '@life/minigames';
import { useEffect, useState } from 'preact/hooks';
import { HelpBlock } from '../components/HelpBlock';
import { Bargain } from './Bargain';
import { Bets } from './Bets';
import { Blackjack } from './Blackjack';
import { Border } from './Border';
import { Budget } from './Budget';
import { Bullying } from './Bullying';
import { Busking } from './Busking';
import { Clinic } from './Clinic';
import { Cooking } from './Cooking';
import { Crypto } from './Crypto';
import { Dice } from './Dice';
import { Driving } from './Driving';
import { Exam } from './Exam';
import { Field } from './Field';
import { FirstLove } from './FirstLove';
import { Interview } from './Interview';
import { Legacy } from './Legacy';
import { Lesson } from './Lesson';
import { Loans } from './Loans';
import { Lottery } from './Lottery';
import { Memory } from './Memory';
import { Night } from './Night';
import { Pyramid } from './Pyramid';
import { Refusal } from './Refusal';
import { Roulette } from './Roulette';
import { Shift } from './Shift';
import { Slots } from './Slots';
import { Stocks } from './Stocks';
import { Suitcase } from './Suitcase';
import { Talk } from './Talk';
import { anyScreen, type AnyScreen, type MinigameScreen } from './types';
import { Wedding } from './Wedding';
import { Yard } from './Yard';

export type { MinigameScreen, MinigameScreenProps } from './types';

/** Сколько секунд даётся на мини-игру (interactives.md: «≤ 30 секунд»). */
export const HOST_SECONDS = 30;

/** Экраны по id мини-игры (раздел E — Task 9; F и G — Task 10). */
export const SCREENS: Record<string, AnyScreen> = {
  lottery: anyScreen(Lottery),
  roulette: anyScreen(Roulette),
  slots: anyScreen(Slots),
  blackjack: anyScreen(Blackjack),
  dice: anyScreen(Dice),
  bets: anyScreen(Bets),
  pyramid: anyScreen(Pyramid),
  crypto: anyScreen(Crypto),
  stocks: anyScreen(Stocks),
  loans: anyScreen(Loans),
  exam: anyScreen(Exam),
  interview: anyScreen(Interview),
  bargain: anyScreen(Bargain),
  field: anyScreen(Field),
  driving: anyScreen(Driving),
  busking: anyScreen(Busking),
  cooking: anyScreen(Cooking),
  yard: anyScreen(Yard),
  lesson: anyScreen(Lesson),
  bullying: anyScreen(Bullying),
  firstLove: anyScreen(FirstLove),
  wedding: anyScreen(Wedding),
  night: anyScreen(Night),
  talk: anyScreen(Talk),
  clinic: anyScreen(Clinic),
  suitcase: anyScreen(Suitcase),
  border: anyScreen(Border),
  shift: anyScreen(Shift),
  budget: anyScreen(Budget),
  refusal: anyScreen(Refusal),
  legacy: anyScreen(Legacy),
  memory: anyScreen(Memory),
};

export interface HostProps<I = unknown, S = unknown> {
  def: MinigameDef<I, S>;
  state: S;
  life: Life;
  /** Rng для `def.play`; стор даёт fork от seed жизни. */
  rng: Rng;
  onDone: (outcome: MinigameOutcome) => void;
  /** Тяжёлая тема: блок помощи всегда на экране. */
  heavy?: boolean;
  /** «Пропускать тяжёлое»: экраны прячут описания рисков. */
  skipHeavy?: boolean;
  /** Подмена экрана (тесты); по умолчанию — `SCREENS[def.id]`. */
  screen?: MinigameScreen<I, S>;
}

/** Ключ заголовка мини-игры. */
export function titleKey(id: string): string {
  return `minigame.title.${id}`;
}

export function Host<I, S>({ def, state, life, rng, onDone, heavy = false, skipHeavy = false, screen }: HostProps<I, S>) {
  const [left, setLeft] = useState(HOST_SECONDS);
  const [done, setDone] = useState(false);

  const finish = (outcome: MinigameOutcome) => {
    if (done) return;
    setDone(true);
    onDone(outcome);
  };

  useEffect(() => {
    if (done) return;
    let remaining = HOST_SECONDS;
    setLeft(remaining);
    const id = setInterval(() => {
      remaining -= 1;
      if (remaining > 0) {
        setLeft(remaining);
        return;
      }
      clearInterval(id);
      finish(def.expected(state, life));
    }, 1000);
    return () => clearInterval(id);
    // Таймер привязан к мини-игре, не к ссылке на onDone.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [def.id, done]);

  const Screen = screen ?? (SCREENS[def.id] as MinigameScreen<I, S> | undefined);
  const expectancy = def.expectancyText?.(state);

  return (
    <section class="minigame" aria-live="polite" data-minigame={def.id}>
      <h2 class="t">{t(titleKey(def.id))}</h2>
      {heavy && <HelpBlock />}
      {expectancy && <p class="expect">{t(expectancy.key, expectancy.params)}</p>}
      <div class="body">
        {Screen ? (
          <Screen
            def={def}
            state={state}
            life={life}
            skipHeavy={skipHeavy}
            onInput={(input: I, played?: S) => finish(def.play(played ?? state, input, life, rng))}
          />
        ) : (
          <p class="hint">{t('ui.minigame.noScreen')}</p>
        )}
      </div>
      <div class="minigame-foot">
        <span class="timer">{t('ui.minigame.timer', { s: left })}</span>
        <button type="button" class="btn ghost" disabled={done} onClick={() => finish(def.expected(state, life))}>
          {t('ui.minigame.skip')}
        </button>
      </div>
    </section>
  );
}
